import type { SessionStatus } from "./api";

export type OperationsReportFilters = {
  from: string;
  to: string;
  locationIds: number[];
  roomIds: number[];
  trainerIds: number[];
  resourceIds: number[];
  statuses: SessionStatus[];
  /** Course-code values, never the UI course option key. */
  courseCodes: string[];
};

export type FilterChoice = { id: number; label: string };

function isValidCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return year >= 2000 && year <= 2100 && candidate.getUTCFullYear() === year && candidate.getUTCMonth() === month - 1 && candidate.getUTCDate() === day;
}

/** Validates an inclusive YYYY-MM-DD date range before it is sent to the API. */
export function isValidDateRange(from: string, to: string): boolean {
  return isValidCalendarDate(from) && isValidCalendarDate(to) && from <= to;
}

/** Serializes the API's AND-across, OR-within filter categories. */
export function buildOperationsReportQuery(filters: OperationsReportFilters): URLSearchParams {
  const query = new URLSearchParams({ from: filters.from, to: filters.to });
  const categories: Array<[keyof Pick<OperationsReportFilters, "locationIds" | "roomIds" | "trainerIds" | "resourceIds">, string]> = [
    ["locationIds", "locationIds"],
    ["roomIds", "roomIds"],
    ["trainerIds", "trainerIds"],
    ["resourceIds", "resourceIds"],
  ];
  for (const [property, parameter] of categories) {
    if (filters[property].length) query.set(parameter, filters[property].join(","));
  }
  query.set("statuses", filters.statuses.join(","));
  if (filters.courseCodes.length) query.set("courseCodes", filters.courseCodes.join(","));
  return query;
}

/**
 * Keeps a checked value operable when dependent choices (for example rooms after
 * choosing a location) are narrowed. `selectedLabels` supplies its last known label.
 */
export function mergeSelectedChoices(choices: FilterChoice[], selectedIds: number[], selectedLabels: Map<number, string>): FilterChoice[] {
  const known = new Set(choices.map((choice) => choice.id));
  return [
    ...choices,
    ...selectedIds.filter((id) => !known.has(id)).map((id) => ({ id, label: selectedLabels.get(id) ?? `ID ${id} (ausgewählt)` })),
  ];
}
